import { useEffect, useState, useRef, useCallback } from 'react';

const style = {
  position: 'absolute',
  top: 0,
  left: 0,
  opacity: 0,
  overflow: 'hidden',
  zIndex: -999,
  padding: '0 15px',
};

function getAttributesObject(node) {
  return {
    tagName: node.tagName,
    text: node.tagName === 'AUDIO' ? null : node.innerText,
    url: node.src,
    height: node.offsetHeight,
    width: node.offsetWidth,
    y: node.offsetTop,
    x: node.offsetLeft,
    className: node.className,
    style: node.attributes.style?.textContent,
  };
}

function useLoadImages(contentRef, rerender) {
  const [loadedImagesCount, setLoadedImages] = useState(0);
  const images = useRef([]);

  const imageLoad = useCallback(() => setLoadedImages(state => state + 1), []);

  useEffect(() => {
    const containerNode = contentRef.current;
    images.current = containerNode.querySelectorAll('img');

    images.current.forEach(image => {
      if (image.width > 0 && image.height > 0) imageLoad();
      else {
        image.addEventListener('load', imageLoad);
        image.addEventListener('error', imageLoad);
      }
    });

    return () => {
      images.current.forEach(image => {
        image.removeEventListener('load', imageLoad);
        image.removeEventListener('error', imageLoad);
      });
    };
  }, [contentRef, imageLoad, rerender]);

  return { images, loadedImagesCount };
}

export function useBluredItems(blurAmount, rerender, cleared, contentRef) {
  const [erased, setErased] = useState(false);
  const [attributes, setAttributes] = useState(null);
  const [showChildren, setShowChildren] = useState(cleared);
  const timeout = useRef(null);

  const { images, loadedImagesCount } = useLoadImages(contentRef, rerender);

  useEffect(() => {
    const containerNode = contentRef.current;
    const paragraphs = containerNode.querySelectorAll('p');
    const audios = containerNode.querySelectorAll('audio');

    if (loadedImagesCount >= images.current.length) {
      setAttributes({
        width: Math.max(containerNode.offsetWidth, 280),
        height: containerNode.offsetHeight,
        items: [...images.current, ...paragraphs, ...audios].map(getAttributesObject),
        blurAmount,
      });
    }
  }, [blurAmount, loadedImagesCount, rerender, contentRef]);

  useEffect(() => {
    if (erased) timeout.current = setTimeout(() => setShowChildren(true), 2000);

    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
  }, [erased]);

  return { attributes, style, erased, setErased, showChildren };
}
